import { useEffect, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Truck as TruckIcon, RefreshCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const jobIcon = (jobId, opts = {}) => {
  const done = opts === true || opts.done;
  const pulse = typeof opts === "object" && opts.pulse;
  const bg = (typeof opts === "object" && opts.color) || (done ? "#16a34a" : "#2563eb");
  return L.divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center;">
      <div style="background:${bg};color:#fff;font-weight:800;font-size:10px;padding:3px 8px;border-radius:999px;box-shadow:0 4px 12px rgba(0,0,0,.3);white-space:nowrap;font-family:monospace;${pulse ? "animation:livePulse 1.6s ease-out infinite;" : ""}">${jobId}</div>
      <div style="width:2px;height:8px;background:${bg};"></div>
      <div style="width:8px;height:8px;border-radius:50%;background:${bg};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);${pulse ? "animation:livePulse 1.6s ease-out infinite;" : ""}"></div>
    </div>`,
    iconAnchor: [0, 40],
    popupAnchor: [0, -40],
  });
};

const moverIcon = (name) =>
  L.divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center;">
      <div style="background:#f59e0b;color:#fff;font-weight:800;font-size:9px;padding:2px 7px;border-radius:999px;box-shadow:0 4px 12px rgba(0,0,0,.3);white-space:nowrap;">${name}</div>
      <div style="width:12px;height:12px;border-radius:50%;background:#f59e0b;border:3px solid #fff;box-shadow:0 0 0 4px rgba(245,158,11,.3);margin-top:2px;"></div>
    </div>`,
    iconAnchor: [0, 30],
    popupAnchor: [0, -30],
  });

export const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 13 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(points)]);
  return null;
};

const MoverRow = ({ m }) => (
  <div className="flex items-center justify-between gap-3 py-1 text-xs">
    <span className="font-semibold text-slate-800">{m.name}</span>
    <span className="text-right">
      {m.lat ? (
        <span className="font-bold text-amber-600" data-testid={`map-mover-km-${m.name.toLowerCase()}`}>
          {m.km} km away{m.updated_at ? ` · ${m.updated_at}` : ""}
        </span>
      ) : (
        <span className="text-slate-400">No live location yet</span>
      )}
      {m.status && (
        <span className={`ml-2 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${m.status === "on_my_way" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
          {m.status === "on_my_way" ? "On My Way" : "Arrived"}
        </span>
      )}
    </span>
  </div>
);

export const AdminLiveMap = () => {
  const [jobs, setJobs] = useState(null);
  const [route, setRoute] = useState(null);
  const tz = "Australia/Melbourne";
  const melbToday = new Date().toLocaleDateString("en-CA", { timeZone: tz });
  const [selectedDate, setSelectedDate] = useState(melbToday);
  const [mapView, setMapView] = useState("street");

  const load = () => axios.get(`${API}/admin/live-map`).then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  const showRoute = (jid) =>
    axios.get(`${API}/route/${jid}`).then(({ data }) => setRoute({ job_id: jid, points: data.points || [] })).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (jobs === null) {
    return <div className="flex justify-center py-16"><RefreshCcw className="h-5 w-5 animate-spin text-blaze" /></div>;
  }

  const fmtDay = (d) => {
    if (!d) return "";
    const today = new Date().toLocaleDateString("en-CA", { timeZone: tz });
    const tmr = new Date(Date.now() + 864e5).toLocaleDateString("en-CA", { timeZone: tz });
    if (d === today) return "Today";
    if (d === tmr) return "Tomorrow";
    try { return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "short" }); }
    catch { return d; }
  };
  const colorFor = (j) => (j.source === "movers_buddy" ? "#7c3aed" : j.is_today ? "#2563eb" : "#0ea5e9");

  // Build a 14-day strip from Melbourne "today"; count jobs per date.
  const countByDate = {};
  (jobs || []).forEach((j) => { countByDate[j.moving_date] = (countByDate[j.moving_date] || 0) + 1; });
  const base = new Date(melbToday + "T00:00:00");
  const strip = Array.from({ length: 14 }, (_, i) => {
    const dt = new Date(base); dt.setDate(base.getDate() + i);
    const ds = dt.toLocaleDateString("en-CA");
    return {
      date: ds,
      weekday: dt.toLocaleDateString("en-AU", { weekday: "short" }),
      dayNum: dt.getDate(),
      month: dt.toLocaleDateString("en-AU", { month: "short" }),
      count: countByDate[ds] || 0,
    };
  });

  // Jobs for the selected day (backend already sorts by date + start time).
  const dayJobs = (jobs || []).filter((j) => j.moving_date === selectedDate);
  const points = [];
  dayJobs.forEach((j) => {
    if (j.pickup_geo) points.push([j.pickup_geo.lat, j.pickup_geo.lng]);
    j.movers.forEach((m) => m.lat && points.push([m.lat, m.lng]));
  });
  const isEmpty = dayJobs.length === 0;
  const connectors = dayJobs.slice(0, -1).map((j, i) => ({
    key: `${j.job_id}-${dayJobs[i + 1].job_id}`,
    positions: [[j.pickup_geo.lat, j.pickup_geo.lng], [dayJobs[i + 1].pickup_geo.lat, dayJobs[i + 1].pickup_geo.lng]],
  }));

  const POPUP_CSS = `
    .leaflet-popup-content-wrapper{background:rgba(255,255,255,0.80)!important;backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);border-radius:16px!important;box-shadow:0 12px 40px rgba(0,0,0,0.18)!important;border:1px solid rgba(255,255,255,0.6);}
    .leaflet-popup-tip{background:rgba(255,255,255,0.80)!important;backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);}
    .leaflet-popup-content{margin:12px 14px!important;}
    .leaflet-container{font-family:inherit;}
  `;

  return (
    <div className="mb-6 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="admin-live-map">
      <style>{POPUP_CSS}</style>
      <div className="flex items-center justify-between px-4 py-3">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blaze">
          <MapPin className="h-4 w-4" /> Live Map
        </p>
        <div className="flex items-center gap-3">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <span className="h-2.5 w-2.5 rounded-full bg-blaze" /> Our job
            <span className="ml-1 h-2.5 w-2.5 rounded-full bg-violet-600" /> Movers Buddy
            <span className="ml-1 h-2.5 w-2.5 rounded-full bg-amber-500" /> Mover live
          </span>
          <button onClick={load} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:text-blaze" data-testid="map-refresh-btn" aria-label="Refresh map">
            <RefreshCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {/* Date strip */}
      <div className="flex gap-2 overflow-x-auto border-t border-slate-100 px-4 py-3" data-testid="map-date-strip" style={{ scrollbarWidth: "thin" }}>
        {strip.map((d) => {
          const active = d.date === selectedDate;
          return (
            <button
              key={d.date}
              onClick={() => setSelectedDate(d.date)}
              data-testid={`map-date-${d.date}`}
              className={`relative flex min-w-[52px] flex-col items-center rounded-xl border px-2.5 py-2 transition-all ${active ? "border-blaze bg-blaze text-white shadow-md" : d.count > 0 ? "border-slate-200 bg-white text-slate-700 hover:border-blaze/50" : "border-slate-100 bg-slate-50 text-slate-300"}`}
            >
              <span className="text-[9px] font-bold uppercase tracking-wider">{d.date === melbToday ? "Today" : d.weekday}</span>
              <span className="text-base font-extrabold leading-tight">{d.dayNum}</span>
              <span className={`text-[8px] font-semibold uppercase ${active ? "text-white/80" : "text-slate-400"}`}>{d.month}</span>
              {d.count > 0 && (
                <span className={`absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold ${active ? "bg-white text-blaze" : "bg-blaze text-white"}`} data-testid={`map-date-count-${d.date}`}>{d.count}</span>
              )}
            </button>
          );
        })}
      </div>
      <div className="relative z-0 h-[calc(100dvh-360px)] min-h-[360px]">
        {/* Street / Satellite toggle (Google-style) */}
        <div className="absolute right-3 top-3 z-[600] flex overflow-hidden rounded-lg border border-slate-300 bg-white shadow-md" data-testid="map-view-toggle">
          <button onClick={() => setMapView("street")} data-testid="map-view-street" className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${mapView === "street" ? "bg-blaze text-white" : "text-slate-600 hover:bg-slate-50"}`}>Map</button>
          <button onClick={() => setMapView("satellite")} data-testid="map-view-satellite" className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${mapView === "satellite" ? "bg-blaze text-white" : "text-slate-600 hover:bg-slate-50"}`}>Satellite</button>
        </div>
        {isEmpty && (
          <div className="absolute inset-x-0 top-0 z-[500] flex justify-center p-3" data-testid="map-empty">
            <span className="rounded-full bg-white/95 px-4 py-2 text-xs font-semibold text-slate-600 shadow-md backdrop-blur">No jobs on {fmtDay(selectedDate)} — pick another date above.</span>
          </div>
        )}
        <MapContainer center={[-37.8136, 144.9631]} zoom={10} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
          {mapView === "satellite" ? (
            <>
              <TileLayer
                attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, Maxar, Earthstar Geographics'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
            </>
          ) : (
            <TileLayer
              attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, HERE, Garmin, &copy; OpenStreetMap contributors'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          )}
          <FitBounds points={points} />
          {connectors.map((c) => (
            <Polyline key={c.key} positions={c.positions} pathOptions={{ color: "#94a3b8", weight: 2.5, opacity: 0.7, dashArray: "6 8" }} />
          ))}
          {dayJobs.map((j) => (
            <Marker
              key={j.job_id}
              position={[j.pickup_geo.lat, j.pickup_geo.lng]}
              icon={jobIcon(j.job_id, { pulse: j.movers.some((m) => m.lat), color: colorFor(j) })}
              eventHandlers={{ click: () => showRoute(j.job_id) }}
            >
              <Popup minWidth={240}>
                <div style={{ fontFamily: "inherit" }} data-testid={`map-popup-${j.job_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="m-0 font-mono text-sm font-bold" style={{ color: colorFor(j) }}>{j.job_id}</p>
                    {j.source === "movers_buddy" && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700" data-testid={`map-mb-badge-${j.job_id}`}>Movers Buddy</span>}
                  </div>
                  <p className="m-0 mt-1 text-xs font-semibold text-slate-700">{fmtDay(j.moving_date)}{j.start_time ? ` · ${j.start_time}` : ""}</p>
                  {j.customer && <p className="m-0 text-xs text-slate-600">{j.customer}</p>}
                  <p className="m-0 mt-2 text-xs"><strong className="text-green-700">Pickup:</strong> {j.pickup}</p>
                  <p className="m-0 text-xs"><strong className="text-red-600">Drop-off:</strong> {j.drop}</p>
                  {j.truck && <p className="m-0 mt-1 flex items-center gap-1 text-xs text-slate-600"><TruckIcon className="h-3 w-3" /> {j.truck}</p>}
                  {j.next_job && (
                    <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-slate-100/80 px-2 py-1.5" data-testid={`map-next-job-${j.job_id}`}>
                      <TruckIcon className="mt-0.5 h-3 w-3 shrink-0 text-slate-500" />
                      <p className="m-0 text-[11px] text-slate-700">Drop-off → next job <strong className="font-mono">{j.next_job.job_id}</strong> pickup — <strong>{j.next_job.km} km</strong> · {j.next_job.mins} min drive</p>
                    </div>
                  )}
                  {j.source === "movers_buddy" ? (
                    <div className="mt-2 border-t border-slate-200 pt-1.5">
                      <p className="m-0 text-xs text-violet-700">Not imported yet — import from Movers Buddy to assign movers.</p>
                    </div>
                  ) : (
                    <div className="mt-2 border-t border-slate-200 pt-1.5">
                      <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">Assigned Movers</p>
                      {j.movers.length === 0 ? (
                        <p className="m-0 py-1 text-xs text-slate-400">No movers assigned yet</p>
                      ) : (
                        j.movers.map((m) => <MoverRow key={m.name} m={m} />)
                      )}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
          {dayJobs.flatMap((j) =>
            j.movers
              .filter((m) => m.lat)
              .map((m) => (
                <Marker key={`${j.job_id}-${m.name}`} position={[m.lat, m.lng]} icon={moverIcon(m.name)}>
                  <Popup>
                    <p className="m-0 text-xs font-bold text-slate-800">{m.name}</p>
                    <p className="m-0 text-xs text-slate-600">{m.km} km from {j.job_id} pickup{m.updated_at ? ` · updated ${m.updated_at}` : ""}</p>
                  </Popup>
                </Marker>
              ))
          )}
          {route && route.points.length > 0 && (
            <>
              <Polyline positions={route.points} pathOptions={{ color: "#2563eb", weight: 5, opacity: 0.85 }} />
              <Marker position={route.points[route.points.length - 1]} icon={jobIcon("DROP-OFF", { color: "#dc2626" })}>
                <Popup><p className="m-0 text-xs font-bold text-slate-800">Drop-off — {route.job_id}</p></Popup>
              </Marker>
            </>
          )}
        </MapContainer>
      </div>
    </div>
  );
};
